<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert, useTrack } from 'dashboard/composables';
import { CAMPAIGN_TYPES } from 'shared/constants/campaign.js';
import { CAMPAIGNS_EVENTS } from 'dashboard/helper/AnalyticsHelper/events.js';

import WhatsAppCampaignForm from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaign/WhatsAppCampaignForm.vue';

const props = defineProps({
  campaign: {
    type: Object,
    default: null,
  },
  initialDraft: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['close']);

const store = useStore();
const { t } = useI18n();
const editTitle = 'Editar campanha do WhatsApp';
const dialogTitle = computed(() =>
  props.campaign?.id ? editTitle : t(`CAMPAIGN.WHATSAPP.CREATE.TITLE`)
);

const addCampaign = async campaignDetails => {
  try {
    await store.dispatch('campaigns/create', campaignDetails);

    useTrack(CAMPAIGNS_EVENTS.CREATE_CAMPAIGN, {
      type: CAMPAIGN_TYPES.ONE_OFF,
    });

    useAlert(t('CAMPAIGN.WHATSAPP.CREATE.FORM.API.SUCCESS_MESSAGE'));
    emit('close');
  } catch (error) {
    const errorMessage =
      error?.response?.data?.message ||
      error?.response?.message ||
      t('CAMPAIGN.WHATSAPP.CREATE.FORM.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

const updateCampaign = async campaignDetails => {
  try {
    await store.dispatch('campaigns/update', {
      id: props.campaign.id,
      ...campaignDetails,
    });

    useAlert('Campanha atualizada com sucesso.');
    emit('close');
  } catch (error) {
    const errorMessage =
      error?.response?.data?.message ||
      error?.response?.message ||
      t('CAMPAIGN.WHATSAPP.CREATE.FORM.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

const handleSubmit = campaignDetails => {
  if (props.campaign?.id) {
    updateCampaign(campaignDetails);
    return;
  }

  addCampaign(campaignDetails);
};

const handleClose = () => emit('close');
</script>

<template>
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-n-alpha-black8 p-4 backdrop-blur-sm"
  >
    <div
      class="flex max-h-[92dvh] w-full max-w-5xl min-w-0 flex-col overflow-hidden rounded-2xl border border-n-weak bg-n-solid-2 shadow-2xl"
    >
      <header
        class="flex shrink-0 items-center gap-3 border-b border-n-weak bg-n-solid-1 px-6 py-4"
      >
        <span
          class="grid size-10 shrink-0 place-items-center rounded-xl bg-n-brand/10 text-n-brand"
        >
          <i class="i-lucide-megaphone size-5" />
        </span>
        <h3 class="m-0 text-base font-semibold text-n-slate-12">
          {{ dialogTitle }}
        </h3>
      </header>
      <WhatsAppCampaignForm
        class="min-h-0 flex-1"
        :campaign="props.campaign"
        :initial-draft="props.initialDraft"
        @submit="handleSubmit"
        @cancel="handleClose"
      />
    </div>
  </div>
</template>
